import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CompleteDayEditor, CompleteDayFilters } from '../../models/complete-day.models';
import {
  ProjectWeatherCompleteEditor,
  ProjectWeatherCompleteFilters
} from '../../models/weather-readings/weather-readings.models';
import {
  DailyLegLoadCompleteEditor,
  DailyLegLoadCompleteFilters
} from '../../models/daily-leg-loads/daily-leg-load.models';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { ErrorType, ValidationResult } from '../../models/projects/validation.models';
import { PtLuxonDate } from '../../models/projects/date.models';
import { DateService } from '../date/date.service';
import { AppConfig } from '../../app/app.config';
import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash';
import { TimeZoneService } from '../time-zone/time-zone.service';

@Injectable()
export class CompleteDayService {

  config = AppConfig.settings;

  async getWorkflow(date: PtLuxonDate): Promise<{ canEdit: boolean; canContinue: boolean; activeDate: string }> {
    return this.http.get<any>(`${this.config.apiUrl}api/complete/${date.dateTime.toMillis()}/workflow`).toPromise();
  }

  async continueDay(date: PtLuxonDate): Promise<DateTime> {
    const result = await this.http.post<string>(`${this.config.apiUrl}api/complete/${date.dateTime.toMillis()}/continue`, {}).toPromise();
    return this.dateParserService.deserializeDate(result, true, this.timeZoneService);
  }

  editRecentDay(date: PtLuxonDate): Promise<CompleteDayEditor> {
    return this.http.post<CompleteDayEditor>(`${this.config.apiUrl}api/complete/${date.dateTime.toMillis()}/edit-recent`, {}).toPromise();
  }

  constructor(
    private http: HttpClient,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private timeZoneService: TimeZoneService
  ) {
  }

  async getLatestDate(): Promise<DateTime> {
    const url = `${this.config.apiUrl}api/complete/latestday`;
    const isoDateString = await this.http.get<string>(url).toPromise();

    return await this.dateParserService.deserializeDate(isoDateString, true, this.timeZoneService);
  }

  async getLatestPtLuxonDate(): Promise<any> {
    const dateUtc = await this.getLatestDate();
    return new PtLuxonDate(dateUtc);
  }

  // takes the date stored in local storage for the daily log date and verifies that it's still the correct date for that day
  // (due to other users logging tz changes, or the user editing their own local storage, it might have changed)
  async verifyDailyLogDate(dailyLogDate: DateTime): Promise<DateTime> {
    // toMillis returns utc date, so no need to call dateTime.toUTC() first
    const dateLong = dailyLogDate.toMillis();
    const url = `${this.config.apiUrl}api/complete/verifydailylogdate/${dateLong}`;
    const isoDateString = await this.http.get<string>(url).toPromise();

    return await this.dateParserService.deserializeDate(isoDateString, true, this.timeZoneService);
  }

  async getEarliestLoggableDate(): Promise<any> {
    const url = `${this.config.apiUrl}api/complete/earliestloggableday`;

    const dateString = await this.http.get<string>(url).toPromise();
    return await this.dateParserService.deserializeDate(dateString, true, this.timeZoneService);
  }

  checkDay(editor: CompleteDayFilters, propagateErrors = false): Promise<boolean> {
    editor.date = this.dateParserService.removeOffsetFromLuxonDate(editor.date);

    const url = `${this.config.apiUrl}api/complete/check`;

    return this.http
      .post<boolean>(url, editor)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        if (propagateErrors) { throw err; }
        return Promise.resolve(false);
      });
  }

  saveCompleteDay(editor: CompleteDayEditor, saveChangeHistory: boolean): Promise<CompleteDayEditor> {
    // normally we need to remove offsets before sending to API, while also keeping applying this remove to the localTime
    // , but this had been handled separately in configure-project.ts save(), as it covers the special use case where
    // the app is first being set up - so if isDefault (called from configure-project.ts), then ensure that the offset being removed
    // when the DateTime gets serialised doesn't affect the date by setting it to 0
    if (editor.isDefault) {
      editor.ptLuxonDate.setOffset(0, true);
    } else {
      editor.ptLuxonDate.dateTime = this.dateParserService.removeOffsetFromLuxonDate(editor.ptLuxonDate.dateTime);
    }

    const url = `${this.config.apiUrl}api/complete/save/${saveChangeHistory}`;

    return this.http
      .post<Promise<CompleteDayEditor>>(url, editor)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      });
  }

  async updateCompleteDayValidationMetadata(result: ValidationResult, dailyLogDate: PtLuxonDate) {
    const completeDayEditor = new CompleteDayEditor();

    completeDayEditor.errorsJson = JSON.stringify(result.errors);
    completeDayEditor.errorCount = result.errors.filter(e => e.type === ErrorType.Error).length;
    completeDayEditor.warningCount = result.errors.filter(e => e.type === ErrorType.Warning).length;

    completeDayEditor.ptLuxonDate = new PtLuxonDate(await this.dateParserService.removeOffsetFromLuxonDate(cloneDeep(dailyLogDate.dateTime)));

    const url = `${this.config.apiUrl}api/complete/updatevalidationmetadata`;

    return this.http
      .post<Promise<CompleteDayEditor>>(url, completeDayEditor)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new CompleteDayEditor());
      });
  }

  async setDayNotComplete(dailyLogDate: PtLuxonDate): Promise<CompleteDayEditor> {
    // toMillis will remove offset so api receives correct date
    const dateLong = dailyLogDate.dateTime.toMillis();
    const url = `${this.config.apiUrl}api/complete/${dateLong}/setnotcomplete`;

    return await this.http.get<CompleteDayEditor>(url).toPromise();
  }

  async setWeatherReadingDayNotComplete(dailyLogDate: PtLuxonDate): Promise<ProjectWeatherCompleteEditor> {
    // toMillis will remove offset so api receives correct date
    const dateLong = dailyLogDate.dateTime.toMillis();
    const url = `${this.config.apiUrl}api/complete/${dateLong}/setweathernotcomplete`;
    const result = await this.http.get<ProjectWeatherCompleteEditor>(url).toPromise();

    return await this.dateParserService.deserializeDates(result, true, this.timeZoneService);
  }

  async setLegLoadsDayNotComplete(dailyLogDate: PtLuxonDate): Promise<DailyLegLoadCompleteEditor> {
    // toMillis will remove offset so api receives correct date
    const dateLong = dailyLogDate.dateTime.toMillis();
    const url = `${this.config.apiUrl}api/complete/${dateLong}/setlegloadsnotcomplete`;
    const result = await this.http.get<DailyLegLoadCompleteEditor>(url).toPromise();

    return await this.dateParserService.deserializeDates(result, true, this.timeZoneService);
  }

  checkWeatherReadingDay(editor: ProjectWeatherCompleteFilters): Promise<boolean> {
    editor.date = this.dateParserService.removeOffsetFromLuxonDate(editor.date);

    const url = `${this.config.apiUrl}api/complete/weatherreading/check`;
    return this.http
      .post<boolean>(url, editor)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(false);
      });
  }

  completeWeatherReadingDay(editor: ProjectWeatherCompleteEditor): Promise<ProjectWeatherCompleteEditor> {
    editor.date = this.dateParserService.removeOffsetFromLuxonDate(editor.date);

    const url = `${this.config.apiUrl}api/complete/weatherreading/save`;
    return this.http
      .post<Promise<ProjectWeatherCompleteEditor>>(url, editor)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new ProjectWeatherCompleteEditor());
      });
  }

  checkDailyLegLoadDay(editor: DailyLegLoadCompleteFilters): Promise<boolean> {
    editor.date = this.dateParserService.removeOffsetFromLuxonDate(editor.date);

    const url = `${this.config.apiUrl}api/complete/dailylegload/check`;
    return this.http
      .post<boolean>(url, editor)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(false);
      });
  }

  // note that the result.date isn't deserialised, because we dont actually use it anywhere
  async completeDailyLegLoadDay(editor: DailyLegLoadCompleteEditor): Promise<DailyLegLoadCompleteEditor> {
    editor.date = this.dateParserService.removeOffsetFromLuxonDate(editor.date);
    const url = `${this.config.apiUrl}api/complete/dailylegload/save`;
    const result = await this.http.post<Promise<DailyLegLoadCompleteEditor>>(url, editor).toPromise();

    return result;
  }
}



// WEBPACK FOOTER //
// ./src/services/complete-day/complete-day.service.ts